import { AccentVariantEnum } from '@delmonicos/shared-design-system-react/src/components/accent-icon/accent-icon';
import { Card } from '@delmonicos/shared-design-system-react/src/components/card/card';
import { Checkbox } from '@delmonicos/shared-design-system-react/src/components/checkbox/checkbox';
import { Divider } from '@delmonicos/shared-design-system-react/src/components/divider/divider';
import { Icon } from '@delmonicos/shared-design-system-react/src/components/icon/icon';
import { TextInput } from '@delmonicos/shared-design-system-react/src/components/input/text-input';
import {
  GapEnum,
  Stack,
} from '@delmonicos/shared-design-system-react/src/components/stack/stack';
import {
  Text,
  TextColor,
} from '@delmonicos/shared-design-system-react/src/components/typography/text/text';
import { AccentTitle } from '@delmonicos/shared-design-system-react/src/components/typography/title/accent-title/accent-title';
import { useUpdateTariffFormValidation } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/inputs/tariff/update-tariff.form-validation';
import { UpdateTariffModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/inputs/tariff/update-tariff.model';
import { PriceComponentModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/outputs/tariff/price-component.model';
import { TariffDimensionType } from '@delmonicos/shared-sdk/libs/cpms/src/libs/ocpi/src/enums/ocpi-protocol-enums';
import classNames from 'classnames';
import { useEffect, useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  RangeUIModel,
  TimeSlotModel,
  useTariffUIContext,
} from '@app/feature/tariff/tariff.context';

import * as css from './tariff-update-form-step-3.module.scss';

interface PriceSelectionProps {
  elementIndex: number;
  priceComponentIndex: number;
}

const EnergySelection = ({
  elementIndex,
  priceComponentIndex,
}: PriceSelectionProps): React.JSX.Element => {
  const { t } = useTranslation();
  const { getValues, control, setValue } = useFormContext<UpdateTariffModel>();
  const { priceComponentPrice, priceComponentStepSize } =
    useUpdateTariffFormValidation();

  useEffect(() => {
    setValue(
      `tariff.elements.${elementIndex}.priceComponents.${priceComponentIndex}.type`,
      TariffDimensionType.ENERGY,
    );
  }, [elementIndex, priceComponentIndex, setValue]);

  return (
    <Stack gap={GapEnum.M} alignItems="center" flexWrap="wrap">
      <Stack gap={GapEnum.S} alignItems="center">
        <Controller
          name={`tariff.elements.${elementIndex}.priceComponents.${priceComponentIndex}.price`}
          control={control}
          rules={priceComponentPrice(elementIndex, priceComponentIndex)}
          render={({ field, fieldState: { error } }) => {
            return (
              <TextInput
                label={t('delmonicos-shared-sdk.outputs-price-component.price')}
                placeholder="0"
                required
                errorMessage={error?.message}
                type="number"
                {...field}
                value={field.value ?? ''}
                onChange={(e) =>
                  field.onChange(
                    e.target.value === '' ? '' : e.target.valueAsNumber,
                  )
                }
              />
            );
          }}
        />
        <Text color={TextColor.Secondary}>{getValues('tariff.currency')}</Text>
      </Stack>

      <Text color={TextColor.Secondary}>{t('$.tariff-update.step-3.by')}</Text>

      <Stack gap={GapEnum.S} alignItems="center">
        <Controller
          name={`tariff.elements.${elementIndex}.priceComponents.${priceComponentIndex}.stepSize`}
          control={control}
          rules={priceComponentStepSize(elementIndex, priceComponentIndex)}
          render={({ field, fieldState: { error } }) => {
            return (
              <TextInput
                label={t(
                  'delmonicos-shared-sdk.outputs-price-component.step-size',
                )}
                placeholder="0"
                type="number"
                required
                errorMessage={error?.message}
                {...field}
                value={field.value ?? ''}
                onChange={(e) =>
                  field.onChange(
                    e.target.value === '' ? '' : e.target.valueAsNumber,
                  )
                }
              />
            );
          }}
        />
        <Text color={TextColor.Secondary}>kWh</Text>
      </Stack>
    </Stack>
  );
};

const TimeSelection = ({
  elementIndex,
  priceComponentIndex,
}: PriceSelectionProps): React.JSX.Element => {
  const { t } = useTranslation();
  const { getValues, control, setValue } = useFormContext<UpdateTariffModel>();
  const { priceComponentPrice, priceComponentStepSize } =
    useUpdateTariffFormValidation();

  useEffect(() => {
    setValue(
      `tariff.elements.${elementIndex}.priceComponents.${priceComponentIndex}.type`,
      TariffDimensionType.TIME,
    );
  }, [elementIndex, priceComponentIndex, setValue]);

  return (
    <Stack gap={GapEnum.M} alignItems="center" flexWrap="wrap">
      <Stack gap={GapEnum.S} alignItems="center">
        <Controller
          name={`tariff.elements.${elementIndex}.priceComponents.${priceComponentIndex}.price`}
          control={control}
          rules={priceComponentPrice(elementIndex, priceComponentIndex)}
          render={({ field, fieldState: { error } }) => {
            return (
              <TextInput
                label={t('delmonicos-shared-sdk.outputs-price-component.price')}
                placeholder="0"
                errorMessage={error?.message}
                type="number"
                {...field}
                value={field.value ?? ''}
                onChange={(e) =>
                  field.onChange(
                    e.target.value === '' ? '' : e.target.valueAsNumber,
                  )
                }
              />
            );
          }}
        />
        <Text color={TextColor.Secondary}>{getValues('tariff.currency')}</Text>
      </Stack>

      <Text color={TextColor.Secondary}>{t('$.tariff-update.step-3.by')}</Text>

      <Stack gap={GapEnum.S} alignItems="center">
        <Controller
          name={`tariff.elements.${elementIndex}.priceComponents.${priceComponentIndex}.stepSize`}
          control={control}
          rules={priceComponentStepSize(elementIndex, priceComponentIndex)}
          render={({ field, fieldState: { error } }) => {
            return (
              <TextInput
                label={t(
                  'delmonicos-shared-sdk.outputs-price-component.step-size',
                )}
                placeholder="0"
                errorMessage={error?.message}
                type="number"
                {...field}
                value={field.value ?? ''}
                onChange={(e) =>
                  field.onChange(
                    e.target.value === '' ? '' : e.target.valueAsNumber,
                  )
                }
              />
            );
          }}
        />

        <Text color={TextColor.Secondary}>min</Text>
      </Stack>
    </Stack>
  );
};

interface ParkingSelectionProps extends PriceSelectionProps {
  hasParkingPrice: boolean;
  setHasParkingPrice: (value: boolean) => void;
}

const ParkingSelection = ({
  elementIndex,
  priceComponentIndex,
  hasParkingPrice,
  setHasParkingPrice,
}: ParkingSelectionProps): React.JSX.Element => {
  const { t } = useTranslation();
  const { getValues, control, setValue } = useFormContext<UpdateTariffModel>();
  const { priceComponentPrice, priceComponentStepSize } =
    useUpdateTariffFormValidation();

  useEffect(() => {
    setValue(
      `tariff.elements.${elementIndex}.priceComponents.${priceComponentIndex}.type`,
      TariffDimensionType.PARKING_TIME,
    );
  }, [elementIndex, priceComponentIndex, setValue]);

  const handleCheckParkingPrice = (value: boolean) => {
    if (!value) {
      setValue(
        `tariff.elements.${elementIndex}.priceComponents.${priceComponentIndex}.price`,
        // @ts-ignore
        undefined,
      );
      setValue(
        `tariff.elements.${elementIndex}.priceComponents.${priceComponentIndex}.stepSize`,
        // @ts-ignore
        undefined,
      );
    }

    setHasParkingPrice(value);
  };

  return (
    <Stack gap={GapEnum.M} direction="column">
      <Checkbox
        id={`different-tariff-after-charge-${elementIndex}-${priceComponentIndex}`}
        label={t('$.tariff-update.step-3.parking.different-tariff.label')}
        checked={hasParkingPrice}
        onChange={(e) => handleCheckParkingPrice(e.target.checked)}
      />
      {hasParkingPrice && (
        <Stack gap={GapEnum.M} alignItems="center" flexWrap="wrap">
          <Stack gap={GapEnum.S} alignItems="center">
            <Controller
              name={`tariff.elements.${elementIndex}.priceComponents.${priceComponentIndex}.price`}
              control={control}
              rules={priceComponentPrice(elementIndex, priceComponentIndex)}
              render={({ field, fieldState: { error } }) => {
                return (
                  <TextInput
                    label={t(
                      'delmonicos-shared-sdk.outputs-price-component.price',
                    )}
                    placeholder="0"
                    errorMessage={error?.message}
                    type="number"
                    {...field}
                    value={field.value ?? ''}
                    onChange={(e) =>
                      field.onChange(
                        e.target.value === '' ? '' : e.target.valueAsNumber,
                      )
                    }
                  />
                );
              }}
            />
            <Text color={TextColor.Secondary}>
              {getValues('tariff.currency')}
            </Text>
          </Stack>

          <Text color={TextColor.Secondary}>
            {t('$.tariff-update.step-3.by')}
          </Text>

          <Stack gap={GapEnum.S} alignItems="center">
            <Controller
              name={`tariff.elements.${elementIndex}.priceComponents.${priceComponentIndex}.stepSize`}
              control={control}
              rules={priceComponentStepSize(elementIndex, priceComponentIndex)}
              render={({ field, fieldState: { error } }) => {
                return (
                  <TextInput
                    label={t(
                      'delmonicos-shared-sdk.outputs-price-component.step-size',
                    )}
                    placeholder="0"
                    errorMessage={error?.message}
                    type="number"
                    {...field}
                    value={field.value ?? ''}
                    onChange={(e) =>
                      field.onChange(
                        e.target.value === '' ? '' : e.target.valueAsNumber,
                      )
                    }
                  />
                );
              }}
            />
            <Text color={TextColor.Secondary}>min</Text>
          </Stack>
        </Stack>
      )}
    </Stack>
  );
};

interface TariffTimeSlotProps {
  elementIndex: number;
  hasDoubleTimeSlot: boolean;
  timeSlot: TimeSlotModel;
  setTimeSlot: (timeSlot: TimeSlotModel) => void;
  timeSlotIndex: number;
}

const TariffTimeSlot = ({
  elementIndex,
  hasDoubleTimeSlot,
  timeSlot,
  setTimeSlot,
  timeSlotIndex,
}: TariffTimeSlotProps): React.JSX.Element => {
  const { t } = useTranslation();
  const { getValues } = useFormContext<UpdateTariffModel>();

  const setHasParkingPrice = (value: boolean) => {
    const newFirstRange = { ...timeSlot };
    newFirstRange.hasParkingPrice = value;
    setTimeSlot(newFirstRange);
  };

  const priceComponents =
    getValues(`tariff.elements.${elementIndex}.priceComponents`) ?? [];
  const energyIndex = priceComponents.findIndex(
    (p) => p.type === TariffDimensionType.ENERGY,
  );
  const timeIndex = priceComponents.findIndex(
    (p) => p.type === TariffDimensionType.TIME,
  );
  const parkingIndex = priceComponents.findIndex(
    (p) => p.type === TariffDimensionType.PARKING_TIME,
  );

  return (
    <Stack gap={GapEnum.M} direction="column">
      {hasDoubleTimeSlot && (
        <Stack
          justifyContent="space-between"
          gap={GapEnum.M}
          alignItems="center">
          <Stack gap={GapEnum.S} alignItems="center">
            <Icon name="clock" />
            <Text bold>
              {timeSlot.startTime} - {timeSlot.endTime}
            </Text>
          </Stack>
          <Text color={TextColor.Secondary}>
            {t('$.tariff-update.step-3.range.slot.title', {
              number: timeSlotIndex,
            })}
          </Text>
        </Stack>
      )}
      <Stack gap={GapEnum.S} flex={1}>
        <Card flex={1} gap={GapEnum.M}>
          <AccentTitle
            icon="lightning-01"
            text={t('$.tariff-update.step-3.range.slot.energy.title')}
            variant={
              elementIndex < 2
                ? AccentVariantEnum.Secondary
                : AccentVariantEnum.Accent1
            }
          />
          <EnergySelection
            elementIndex={elementIndex}
            priceComponentIndex={
              energyIndex >= 0 ? energyIndex : priceComponents.length
            }
          />
        </Card>

        <div style={{ alignContent: 'center' }}>
          <Text>+</Text>
        </div>

        <Card flex={1} gap={GapEnum.M} direction="column">
          <AccentTitle
            icon="clock-fast-forward"
            text={t('$.tariff-update.step-3.range.slot.time.title')}
            variant={
              elementIndex < 2
                ? AccentVariantEnum.Secondary
                : AccentVariantEnum.Accent1
            }
          />
          <TimeSelection
            elementIndex={elementIndex}
            priceComponentIndex={
              timeIndex >= 0 ? timeIndex : priceComponents.length + 1
            }
          />
          <Divider />
          <ParkingSelection
            elementIndex={elementIndex}
            priceComponentIndex={
              parkingIndex >= 0 ? parkingIndex : priceComponents.length + 2
            }
            hasParkingPrice={timeSlot.hasParkingPrice}
            setHasParkingPrice={setHasParkingPrice}
          />
        </Card>
      </Stack>
    </Stack>
  );
};

interface TariffRangeProps {
  elementIndexOffset: number;
  range: RangeUIModel;
  setRange: (value: RangeUIModel) => void;
}

const TariffRange = ({
  elementIndexOffset,
  range,
  setRange,
}: TariffRangeProps): React.JSX.Element => {
  const { t } = useTranslation();

  const title = useMemo(
    () =>
      range.daysOfWeek
        ?.map((day) =>
          t(`delmonicos-shared-sdk.ocpi-protocol-enums.day-of-week.${day}`),
        )
        .join(' - '),
    [t, range.daysOfWeek],
  );

  const setFirstTimeSlot = (newTimeSlot: TimeSlotModel) => {
    setRange({ ...range, firstTimeSlot: newTimeSlot });
  };

  const setSecondTimeSlot = (newTimeSlot: TimeSlotModel) => {
    setRange({ ...range, secondTimeSlot: newTimeSlot });
  };

  return (
    <Card flex={1} gap={GapEnum.M}>
      <Stack justifyContent="space-between" gap={GapEnum.M} alignItems="center">
        <AccentTitle
          icon="calendar-date"
          text={title ?? ''}
          variant={
            elementIndexOffset < 2
              ? AccentVariantEnum.Secondary
              : AccentVariantEnum.Accent1
          }
        />
        <Text color={TextColor.Secondary}>
          {t('$.tariff-update.step-3.range.title', {
            number: Math.floor(elementIndexOffset / 2) + 1,
          })}
        </Text>
      </Stack>

      <TariffTimeSlot
        elementIndex={elementIndexOffset}
        hasDoubleTimeSlot={range.hasDoubleTimeSlot}
        timeSlot={range.firstTimeSlot}
        setTimeSlot={setFirstTimeSlot}
        timeSlotIndex={1}
      />

      {range.hasDoubleTimeSlot && (
        <>
          <Divider />
          <TariffTimeSlot
            elementIndex={elementIndexOffset + 1}
            hasDoubleTimeSlot={range.hasDoubleTimeSlot}
            timeSlot={range.secondTimeSlot}
            setTimeSlot={setSecondTimeSlot}
            timeSlotIndex={2}
          />
        </>
      )}
    </Card>
  );
};

interface PreviewCardTimeSlotProps {
  timeSlot: TimeSlotModel;
  energy?: PriceComponentModel;
  time?: PriceComponentModel;
  parking?: PriceComponentModel;
  currency: string;
  colorVariation?: 'border-secondary' | 'border-accent-1';
}

const PreviewCardTimeSlot = ({
  timeSlot,
  energy,
  time,
  parking,
  currency,
  colorVariation = 'border-secondary',
}: PreviewCardTimeSlotProps): React.JSX.Element => {
  const { t } = useTranslation();
  const { flatValue } = useTariffUIContext();

  return (
    <Card
      flex={1}
      gap={GapEnum.M}
      className={classNames(css.timeSlotPreview, css[colorVariation])}>
      <Stack justifyContent="space-between" alignItems="center">
        <Stack gap={GapEnum.S} alignItems="center">
          <Icon name="price" />
          <Text bold>
            {t('$.tariff-update.step-3.preview.range.slot.tariff')}
          </Text>
        </Stack>
        {timeSlot.endTime && timeSlot.startTime && (
          <Text bold>
            {timeSlot.startTime} - {timeSlot.endTime}
          </Text>
        )}
      </Stack>

      <Stack gap={GapEnum.S} flex={1}>
        <Card
          flex={1}
          gap={GapEnum.M}
          alignItems="center"
          justifyContent="center">
          <Text bold color={TextColor.Secondary}>
            {energy?.price ?? '-'} {currency} / {energy?.stepSize ?? '-'} kWh
          </Text>
        </Card>

        <Stack alignItems="center">
          <Text bold>+</Text>
        </Stack>

        <Card
          flex={1}
          gap={GapEnum.M}
          alignItems="center"
          justifyContent="center">
          <Stack flex={1} alignItems="center" justifyContent="center">
            <Text bold color={TextColor.Secondary}>
              {time?.price ?? '-'} {currency} / {time?.stepSize ?? '-'} min
            </Text>
          </Stack>

          {timeSlot.hasParkingPrice && (
            <Stack
              flex={1}
              alignItems="center"
              justifyContent="center"
              className={css.parkingPreview}>
              <Text color={TextColor.Secondary}>
                {parking?.price ?? '-'} {currency} / {parking?.stepSize ?? '-'}{' '}
                min
              </Text>
            </Stack>
          )}
        </Card>
      </Stack>

      {flatValue && (
        <Stack justifyContent="space-between" alignItems="center">
          <Stack gap={GapEnum.S} alignItems="center">
            <Icon name="home-03" />
            <Text>{t('$.tariff-update.step-3.preview.flat-value')}</Text>
          </Stack>
          <Text bold>
            {flatValue} {currency}
          </Text>
        </Stack>
      )}
    </Card>
  );
};

interface PreviewCardRangeFragmentProps {
  hasDoubleTimeSlot: boolean;
  range: RangeUIModel;
  elementIndexOffset: number;
}

const PreviewCardRangeFragment = ({
  hasDoubleTimeSlot,
  range,
  elementIndexOffset,
}: PreviewCardRangeFragmentProps): React.JSX.Element => {
  const { t } = useTranslation();
  const { getValues, watch } = useFormContext<UpdateTariffModel>();
  const firstTimeSlotPriceComponents = watch(
    `tariff.elements.${elementIndexOffset}.priceComponents`,
  );
  const secondTimeSlotPriceComponents = watch(
    `tariff.elements.${elementIndexOffset + 1}.priceComponents`,
  );

  const firstTimeSlotEnergy = firstTimeSlotPriceComponents?.find(
    (pc) => pc.type === TariffDimensionType.ENERGY,
  );
  const firstTimeSlotTime = firstTimeSlotPriceComponents?.find(
    (pc) => pc.type === TariffDimensionType.TIME,
  );
  const firstTimeSlotParking = firstTimeSlotPriceComponents?.find(
    (pc) => pc.type === TariffDimensionType.PARKING_TIME,
  );
  const secondTimeSlotEnergy = secondTimeSlotPriceComponents?.find(
    (pc) => pc.type === TariffDimensionType.ENERGY,
  );
  const secondTimeSlotTime = secondTimeSlotPriceComponents?.find(
    (pc) => pc.type === TariffDimensionType.TIME,
  );
  const secondTimeSlotParking = secondTimeSlotPriceComponents?.find(
    (pc) => pc.type === TariffDimensionType.PARKING_TIME,
  );

  return (
    <Card gap={GapEnum.M} className={css.previewCard}>
      <Stack justifyContent="space-between" alignItems="center">
        <AccentTitle
          icon="eye"
          text={t('$.tariff-update.step-3.preview.title')}
          variant={
            elementIndexOffset < 2
              ? AccentVariantEnum.Secondary
              : AccentVariantEnum.Accent1
          }
        />
        <Text color={TextColor.Secondary}>
          {t('$.tariff-update.step-3.preview.range.title', {
            number: Math.floor(elementIndexOffset / 2) + 1,
          })}
        </Text>
      </Stack>

      {hasDoubleTimeSlot && (
        <Text color={TextColor.Secondary}>
          {t('$.tariff-update.step-3.preview.range.slot.title', {
            number: 1,
          })}
        </Text>
      )}
      <PreviewCardTimeSlot
        energy={firstTimeSlotEnergy}
        parking={firstTimeSlotParking}
        time={firstTimeSlotTime}
        timeSlot={range.firstTimeSlot}
        currency={getValues('tariff.currency')!}
        colorVariation={
          elementIndexOffset < 2 ? 'border-secondary' : 'border-accent-1'
        }
      />

      {hasDoubleTimeSlot && (
        <>
          <Divider />

          <Text color={TextColor.Secondary}>
            {t('$.tariff-update.step-3.preview.range.slot.title', {
              number: 2,
            })}
          </Text>
          <PreviewCardTimeSlot
            energy={secondTimeSlotEnergy}
            parking={secondTimeSlotParking}
            time={secondTimeSlotTime}
            timeSlot={range.secondTimeSlot}
            currency={getValues('tariff.currency')!}
            colorVariation={
              elementIndexOffset < 2 ? 'border-secondary' : 'border-accent-1'
            }
          />
        </>
      )}
    </Card>
  );
};

export const TariffUpdateFormStep3Component = (): React.JSX.Element => {
  const { firstRange, setFirstRange, secondRange, setSecondRange } =
    useTariffUIContext();

  const hasSecondRange = useMemo(() => {
    return (
      secondRange.hasDoubleTimeSlot ||
      (!!secondRange.daysOfWeek && secondRange.daysOfWeek.length !== 0)
    );
  }, [secondRange]);

  return (
    <Stack gap={GapEnum.M} direction="column" flex={1}>
      <Stack gap={GapEnum.M} flex={1}>
        <TariffRange
          range={firstRange}
          setRange={setFirstRange}
          elementIndexOffset={0}
        />

        <PreviewCardRangeFragment
          range={firstRange}
          hasDoubleTimeSlot={firstRange.hasDoubleTimeSlot}
          elementIndexOffset={0}
        />
      </Stack>

      {hasSecondRange && (
        <>
          <Divider />

          <Stack gap={GapEnum.M} flex={1}>
            <TariffRange
              range={secondRange}
              setRange={setSecondRange}
              elementIndexOffset={2}
            />

            <PreviewCardRangeFragment
              range={secondRange}
              hasDoubleTimeSlot={secondRange.hasDoubleTimeSlot}
              elementIndexOffset={2}
            />
          </Stack>
        </>
      )}
    </Stack>
  );
};
